/**
 * The countries the tenant operates in, as guests read them: "Malaysia &
 * Singapore" for Asian Trails, "Thailand" for a company in one country.
 *
 * Read from the running geography (`opsGeo.ts`): every active destination
 * lists the ISO countries it covers (`ops_destinations.countries`), in the
 * destinations' order, and a country's name is the English one
 * (`Intl.DisplayNames`). Sub-destinations of one country (Borneo inside
 * Malaysia, Phuket inside Thailand) add no country. Until the geography
 * answers it is Asian Trails' built-in one (MY, SG), so the guest texts read
 * exactly as before.
 */
import type { Geo } from './geo';
import { getOpsGeo } from './opsGeo';

const norm = (v: string | null | undefined) => (v ?? '').trim().toUpperCase();
const ISO2 = /^[A-Z]{2}$/;

/** ISO codes of the countries the tenant operates in, in destination order. */
export function tenantCountries(geo: Geo = getOpsGeo()): string[] {
  const out: string[] = [];
  const add = (c: string) => {
    if (ISO2.test(c) && !out.includes(c)) out.push(c);
  };
  for (const d of geo.destinations) {
    const listed = (d.countries ?? []).map(norm).filter((c) => ISO2.test(c));
    if (listed.length > 0) listed.forEach(add);
    else add(norm(d.entity_code) || norm(d.code));
  }
  return out;
}

/** Does the tenant operate in this country (ISO code)? */
export function operatesIn(country: string, geo: Geo = getOpsGeo()): boolean {
  return tenantCountries(geo).includes(norm(country));
}

let displayNames: Intl.DisplayNames | null | undefined;

function intlName(code: string): string | null {
  if (displayNames === undefined) {
    try {
      displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
    } catch {
      displayNames = null;
    }
  }
  try {
    const name = displayNames?.of(code);
    return name && name !== code ? name : null;
  } catch {
    return null;
  }
}

/** "Malaysia" for MY; the destination's label, else the code, when the code is not a country. */
export function countryName(code: string, geo: Geo = getOpsGeo()): string {
  const c = norm(code);
  return intlName(c) ?? (geo.destination(c)?.label?.trim() || c);
}

/** ["A"] → "A"; ["A", "B"] → "A & B"; ["A", "B", "C"] → "A, B & C". */
export function joinNames(names: readonly string[], conjunction: string): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} ${conjunction} ${names[names.length - 1]}`;
}

/**
 * The tenant's countries in words: "Malaysia & Singapore" (`&`), "Malaysia
 * and Singapore" (`and`), "Malaysia or Singapore" (`or`); "Thailand" for one.
 */
export function countriesPhrase(conjunction: '&' | 'and' | 'or' = '&', geo: Geo = getOpsGeo()): string {
  return joinNames(tenantCountries(geo).map((c) => countryName(c, geo)), conjunction);
}

/** A word-bounded, case-insensitive match of any of the tenant's country names. */
export function countryNamesRe(geo: Geo = getOpsGeo()): RegExp | null {
  const names = tenantCountries(geo).map((c) => countryName(c, geo)).filter(Boolean);
  if (names.length === 0) return null;
  const esc = names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  return new RegExp(`\\b(${esc.join('|')})\\b`, 'i');
}
